<template>
  <div
    :class="['categoryNav', { 'categoryNav--popup': isPopup }]"
    @mouseleave="hideContent()"
    v-if="categories"
  >
    <div :class="{ 'categoryNav__inner--active': activeContentIndex }" class="categoryNav__inner">
      <div class="categoryNav__title" v-if="!isPopup">
        {{ $t('categories') }}
      </div>
      <ul class="categoryNav__nav">
        <li
          :key="category.id"
          v-for="category in categories"
          :class="{
            'categoryNav__item--active': category.id === activeContentIndex,
            'categoryNav__item--disabled': !category.isUserAllowedToOrderItems,
          }"
          class="categoryNav__item"
          @mouseenter="showContent(category.id)"
        >
          <nuxt-link
            :to="localePath({ name: 'category-slug', params: { slug: category.slug } })"
            class="categoryNav__link"
            @click.native="close()"
          >
            <span class="categoryNav__linkIcon" :class="`${category.iconClass}`" />
            {{ category.name }}
          </nuxt-link>
        </li>
        <li>
          <nuxt-link
            :to="localePath({ name: 'promotions' })"
            class="categoryNav__link categoryNav__link--disableArrow"
          >
            Promocje
          </nuxt-link>
        </li>
      </ul>
    </div>
    <div
      :class="{ 'categoryNav__content--active': category.id === activeContentIndex }"
      :key="category.id"
      ref="categoryNavContent"
      v-for="category in categories"
      class="categoryNav__content"
    >
      <div class="categoryNav__contentLeft">
        <div class="categoryNav__contentListTitle" v-if="!isPopup">
          {{ category.name }}
        </div>
        <div class="categoryNav__search" v-if="activeContentIndex === 10540">
          <search-toners-inks @submit="$emit('close')" />
        </div>
        <MasonryWall
          :items="category.children"
          :column-width="activeContentIndex === 10540 ? 300 : 200"
          :gap="10"
          :ssr-columns="1"
          class="homeCategories__wrapper"
          ref="wrapper"
        >
          <template v-slot:default="{ item }">
            <div :key="item.id" class="categoryNav__contentItem">
              <nuxt-link
                :to="localePath({ name: 'category-slug', params: { slug: item.slug } })"
                class="categoryNav__contentLink categoryNav__contentLink--bold"
                @click.native="close()"
              >
                {{ item.name }}
              </nuxt-link>
              <ul v-if="item.children && item.children.length">
                <li v-for="nestedChild in item.children" :key="nestedChild.id">
                  <nuxt-link
                    :to="localePath({ name: 'category-slug', params: { slug: nestedChild.slug } })"
                    :class="{
                      'categoryNav__contentLink--disabled': !nestedChild.isUserAllowedToOrderItems,
                    }"
                    class="categoryNav__contentLink"
                    @click.native="close()"
                  >
                    {{ nestedChild.name }}
                  </nuxt-link>
                </li>
              </ul>
            </div>
          </template>
        </MasonryWall>
        <div
          v-if="category.recommendedBrands && category.id === activeContentIndex"
          class="categoryNav__contentListBrands"
        >
          <category-brands :brands="category.recommendedBrands" :is-popup="isPopup" />
        </div>
      </div>
      <div v-if="category.recommendedItem" class="categoryNav__contentBanner">
        <div class="categoryNav__contentBannerInner">
          <div class="categoryNav__contentBannerTitle">Rekomendowane przez ofix</div>
          <div class="categoryNav__contentBannerImage">
            <LazyHydrate never :trigger-hydration="category.id === activeContentIndex">
              <new-image-box
                :alt="category.recommendedItem.name"
                :src="category.recommendedItem.mainPictureUrl"
                :width="150"
                :height="150"
                image-params="w_150,h_150"
              />
            </LazyHydrate>
          </div>
          <div class="categoryNav__contentBannerName" v-line-clamp:20="2">
            {{ category.recommendedItem.name }}
          </div>
          <v-button
            text="Sprawdź produkt"
            :to="
              localePath({
                name: 'product-slug',
                params: { slug: category.recommendedItem.slug },
              })
            "
            theme="purple"
            full-width
            class="categoryNav__contentBannerButton"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MasonryWall from '@yeger/vue2-masonry-wall';
import LazyHydrate from 'vue-lazy-hydration';
import CategoryBrands from './CategoryBrands';
import NewImageBox from '@/components/NewImageBox';
import VButton from '~/components/VButton';
import SearchTonersInks from '~/containers/Search/SearchTonersInks';

export default {
  name: 'CategoryNav',
  components: {
    NewImageBox,
    SearchTonersInks,
    CategoryBrands,
    VButton,
    LazyHydrate,
    MasonryWall,
  },
  props: {
    isPopup: {
      type: Boolean,
      default: false,
    },
    categories: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeContentIndex: null,
    };
  },
  methods: {
    showContent(index) {
      this.activeContentIndex = index;
      this.$emit('open', true);
    },
    hideContent() {
      this.activeContentIndex = null;
      this.$emit('open', false);
    },
    close() {
      this.activeContentIndex = null;
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
.categoryNav {
  $root: &;

  &__inner {
    position: relative;
    z-index: 1;
  }

  &__title {
    padding: 2.5rem 0 2.4rem;
    margin: 0 2.5rem 1.7rem;
    font-size: 1.8rem;
    line-height: 2.3rem;
    border-bottom: 1px solid $cAddLight;

    @include rwd('ultra') {
      padding: 1.5rem 0 1.4rem;
      margin: 0 2.5rem 1.4rem;
    }
  }

  &__nav {
    padding-bottom: 2rem;
  }

  &__item {
    position: relative;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      background-image: linear-gradient(-89deg, #ffffff 0%, #f7f8fc 100%);
      opacity: 0;
      transition: opacity $tr;
    }

    &:after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      width: 3px;
      background-color: $cSec;
      border-radius: 0 2px 2px 0;
      opacity: 0;
      transition: opacity $tr;
    }

    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    &--active {
      width: calc(100% + 1px);
      border-right: 1px solid $cWhite;

      &:before,
      &:after {
        opacity: 1;
      }

      #{$root}__link {
        color: $cMainDark;
        border-top: 1px solid $cAddLight;
        border-bottom: 1px solid $cAddLight;
      }

      #{$root}__linkIcon {
        color: $cSec;
      }
    }
  }

  &__link {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0.4rem 2.5rem;
    font-size: 1.6rem;
    line-height: 3.2rem;
    color: $cAddDark02;
    border-top: 1px solid $cWhite;
    border-bottom: 1px solid $cWhite;
    transition: color $tr, border $tr;

    @include rwd('ultra') {
      padding: 0.2rem 2.5rem;
    }

    &:before {
      content: '';
      position: absolute;
      right: 2.5rem;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 4px 0 4px 5px;
      border-color: transparent transparent transparent $cAddDark;
    }

    &--disableArrow {
      color: $cSec;

      &:before {
        display: none;
      }
    }
  }

  &__linkIcon {
    position: relative;
    top: 1px;
    width: 3rem;
    font-size: 1.5rem;
    transition: color $tr;
  }

  &__content {
    overflow: hidden;
    position: absolute;
    left: 21.5%;
    top: 0;
    width: 78.5%;
    min-height: 100%;
    background-color: $cWhite;
    border-left: 1px solid $cAddLight;
    opacity: 0;
    pointer-events: none;
    display: none;
    visibility: hidden;
    //transition: opacity $tr;

    &--active {
      display: flex;
      visibility: visible;
      opacity: 1;
      pointer-events: all;
    }
  }

  &__contentLeft {
    display: flex;
    flex-direction: column;
    flex: 0 0 77.5%;
    max-width: 77.5%;
    background-color: $cWhite;
    padding: 2.3rem 3rem 2rem 3rem;

    @include rwd('ultra') {
      padding: 1.5rem 2.5rem 2rem 2.5rem;
    }

    &--full {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  &__contentListTitle {
    margin-bottom: 2rem;
    padding-bottom: 2.6rem;
    font-size: 1.8rem;
    line-height: 2.3rem;
    font-weight: 700;
    border-bottom: 1px solid $cAddLight;

    @include rwd('ultra') {
      padding-bottom: 1.4rem;
    }
  }

  &__contentLink {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 1.5rem;
    line-height: 2.5rem;
    color: $cMainLight;
    transition: color $tr;

    &--disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    &--bold {
      font-weight: 700;
      margin-bottom: 0.7rem;
    }

    @include rwd('ultra') {
    }

    &:hover {
      color: $cSec;
    }
  }

  &__contentListBrands {
    margin-top: auto;
    padding-top: 2rem;
  }

  &__contentBanner {
    flex: 0 0 22.5%;
    max-width: 22.5%;
    padding-top: 7.2rem;
    padding-right: 2.5rem;

    @include rwd('ultra') {
      padding-top: 5.2rem;
      padding-bottom: 3rem;
    }
  }

  &__contentBannerInner {
    height: auto;
    min-height: 45rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: $cBg;
    padding: 3.5rem 2rem 2rem;
    text-align: center;
  }

  &__contentBannerTitle {
    margin-bottom: 2rem;
    padding-right: 5px;
    padding-left: 5px;
    font-size: 1.6rem;
    line-height: 2rem;
    font-weight: 600;
    text-align: center;

    @include rwd('ultra') {
      margin-bottom: 1.5rem;
    }
  }

  &__contentBannerImage {
    margin-bottom: 2rem;
    max-width: 100%;
  }

  &__contentBannerName {
    font-size: 1.5rem;
    line-height: 2.2rem;
    margin-bottom: 3rem;
    max-height: 4.4rem;
    overflow: hidden;

    @include rwd('ultra') {
      margin-bottom: 1.5rem;
    }
  }

  &__search {
    border-bottom: 1px solid $cAddLight;
    padding-bottom: 3rem;
    padding-top: 0.1rem;
    margin-bottom: 3rem;
  }

  &--popup {
    display: flex;
    max-width: 300px;

    #{$root}__nav {
      padding-top: 2rem;
    }

    //#{$root}__contentLeft {
    //  flex: 0 0 100%;
    //  max-width: 100%;
    //}

    #{$root}__inner {
      flex: 0 0 300px;
      max-width: 300px;
      border: 1px solid $cAddLight;
      border-radius: 4px 0 0 4px;
      background-color: $cWhite;
      height: 100%;
      box-shadow: 0 10px 20px rgba(23, 25, 51, 0.1);

      &--active {
        border-radius: 4px 0 0 4px;
        border-right: 0;
      }
    }

    #{$root}__content {
      flex: 0 0 1100px;
      max-width: calc(100vw - 340px);
      width: 100%;
      max-height: calc(100vh - 15rem);
      overflow: auto;
      border-top: 1px solid $cAddLight;
      border-bottom: 1px solid $cAddLight;
      border-right: 1px solid $cAddLight;
      border-radius: 0 4px 4px 0;
      box-shadow: 0 10px 20px rgba(23, 25, 51, 0.1);

      &--active {
        position: relative;
        left: 0;
      }
    }
  }
}
</style>
